/**
 * PR-D4-042 / PR-D4-052 — Review incorrect answers with compact GGS diagrams.
 */

import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { SessionReviewCard } from '../components/SessionReviewCard';
import { api } from '../lib/api-client';
import { resolveTelemetryUserId } from '../lib/auth-session';
import type { Question, SessionMistakesPayload } from '../lib/types';
import './ReviewSessionPage.css';

export function ReviewMistakesPage() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const sessionId = searchParams.get('sessionId') ?? '';
    const userId = resolveTelemetryUserId();
    const [payload, setPayload] = useState<SessionMistakesPayload | null>(null);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!sessionId) {
            setError('Missing sessionId');
            setLoading(false);
            return;
        }
        let cancelled = false;
        setLoading(true);
        void api
            .getSessionMistakes(sessionId, userId)
            .then((data) => {
                if (!cancelled) {
                    setPayload(data);
                    setError('');
                }
            })
            .catch((err: unknown) => {
                if (!cancelled) {
                    setPayload(null);
                    setError(err instanceof Error ? err.message : 'Failed to load mistakes');
                }
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [sessionId, userId]);

    if (loading) {
        return (
            <div className="review-session-page">
                <div className="review-session-page__hero-inner">
                    <p className="review-session-page__subtitle">Loading mistakes…</p>
                </div>
            </div>
        );
    }

    if (error || !payload) {
        return (
            <div className="review-session-page">
                <div className="review-session-page__hero-inner">
                    <h1 className="review-session-page__title">Could not open review</h1>
                    <p className="review-session-page__subtitle">{error || 'Session not found.'}</p>
                    <button
                        type="button"
                        className="review-session-page__back"
                        onClick={() => navigate('/dashboard')}
                    >
                        Return to Dashboard
                    </button>
                </div>
            </div>
        );
    }

    const questions = payload.questions as Question[];
    const score = payload.session.score;
    const maxScore = payload.session.maxScore;

    return (
        <div className="review-session-page">
            <header className="review-session-page__hero">
                <div className="review-session-page__hero-inner">
                    <p className="review-session-page__brand">SAT Generator</p>
                    <h1 className="review-session-page__title">Работа над ошибками</h1>
                    <p className="review-session-page__subtitle">
                        {payload.session.moduleType} · {payload.session.mode}
                        {score != null && maxScore != null ? ` · score ${score}/${maxScore}` : ''}
                        {` · ${questions.length} incorrect`}
                    </p>
                    <button
                        type="button"
                        className="review-session-page__back"
                        onClick={() => navigate('/dashboard')}
                    >
                        Back to Dashboard
                    </button>
                </div>
            </header>

            <div className="review-session-page__list">
                {questions.length === 0 ? (
                    <p className="review-session-page__subtitle review-session-page__list-heading">
                        No incorrect answers in this session.
                    </p>
                ) : (
                    questions.map((question, idx) => {
                        const result = payload.results.find((r) => r.questionId === question.id);
                        return (
                            <SessionReviewCard
                                key={`${question.id}-${idx}`}
                                question={question}
                                questionNumber={idx + 1}
                                totalQuestions={questions.length}
                                selectedOption={result?.selectedOption ?? null}
                                isCorrect={false}
                                timeSpentSeconds={result?.timeSpentSeconds}
                            />
                        );
                    })
                )}
            </div>
        </div>
    );
}
